<template>
  <!-- カスタムモーダル(Bootstrap Vue Nextのデフォルトボタンを回避) -->
  <div v-if="internalVisible" class="modal fade show" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered" :class="`modal-${size}`" @click.stop>
      <div class="modal-content">
        <!-- モーダルヘッダー -->
        <div class="modal-header" :class="`header-${headerStyle}`">
          <h5 class="modal-title">{{ title }}</h5>
          <button type="button" class="btn-close" @click="handleCancel" aria-label="Close"></button>
        </div>

        <!-- モーダルボディ -->
        <div class="modal-body" :class="{ 'text-center': centerContent }">
          <slot></slot>
        </div>

        <!-- モーダルフッター -->
        <div class="modal-footer">
          <slot name="footer">
            <!-- デフォルトフッター（確認ダイアログ用） -->
            <BButton
              @click="handleCancel"
              class="btn-cancel"
            >
              {{ cancelText }}
            </BButton>
            <BButton
              @click="handleConfirm"
              variant="primary"
            >
              {{ confirmText }}
            </BButton>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue'

/**
 * BaseModal - 汎用的な確認モーダルベースコンポーネント（Moleculesレベルコンポーネント）
 *
 * アトミックデザインに準拠した再利用可能なモーダルベースコンポーネントです。
 * 設計方針に従い、UIの構造とユーザーインタラクションに専念し、
 * 具体的なコンテンツと処理は子コンポーネントや親コンポーネントに委ねます。
 *
 * アトミックデザイン分類: Molecules（分子）
 * - BootstrapVueのb-modalとb-buttonを組み合わせた汎用的な基盤コンポーネント
 * - タイトル、コンテンツ（スロット）、2つのボタンを持つ標準的なモーダル構造
 *
 * 責務:
 * - モーダルの基本構造とレイアウト提供
 * - ボタンクリック時のイベント処理と通知
 * - モーダルの表示/非表示制御
 * - 共通スタイルの適用
 *
 * 外部インターフェース:
 * - Props: modalId, title, cancelText, confirmText
 * - Events: cancel, confirm
 * - Methods: showModal(), hideModal()
 * - Slots: default（モーダルボディのコンテンツ）
 *
 * 使用例:
 * <BaseModal
 *   modal-id="my-modal"
 *   title="確認"
 *   cancel-text="キャンセル"
 *   confirm-text="OK"
 *   @cancel="handleCancel"
 *   @confirm="handleConfirm"
 * >
 *   <p>モーダルの内容をここに記述</p>
 * </BaseModal>
 */

const props = defineProps({
  /**
   * モーダルのユニークID
   * BootstrapVueのb-modalで使用されます
   */
  modalId: {
    type: String,
    required: true
  },

  /**
   * モーダルのタイトル
   */
  title: {
    type: String,
    required: true
  },

  /**
   * キャンセルボタンのテキスト
   * デフォルト: "キャンセル"
   */
  cancelText: {
    type: String,
    default: 'キャンセル'
  },

  /**
   * 確認ボタンのテキスト
   * デフォルト: "OK"
   */
  confirmText: {
    type: String,
    default: 'OK'
  },

  /**
   * モーダルの表示状態（Vue 2互換）
   */
  value: {
    type: Boolean,
    default: false
  },

  /**
   * モーダルの表示状態（Vue 3 v-model）
   */
  modelValue: {
    type: Boolean,
    default: false
  },

  /**
   * モーダルボディのコンテンツを中央寄せするかどうか
   * デフォルト: true（確認ダイアログ用）
   */
  centerContent: {
    type: Boolean,
    default: true
  },

  /**
   * モーダルのサイズ
   * 'sm', 'md', 'lg', 'xl' のいずれか
   */
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg', 'xl'].includes(value)
  },

  /**
   * ヘッダーのスタイル
   * 'default', 'gradient', 'dark' のいずれか
   */
  headerStyle: {
    type: String,
    default: 'default',
    validator: value => ['default', 'gradient', 'dark'].includes(value)
  }
})

const emit = defineEmits(['input', 'update:modelValue', 'cancel', 'confirm'])

/**
 * 内部的なモーダル表示状態
 * v-modelとの双方向バインディング用
 */
const internalVisible = computed({
  get: () => props.modelValue !== undefined ? props.modelValue : props.value,
  set: (visible) => {
    // Vue 2互換のinputイベント
    emit('input', visible)
    // Vue 3のv-modelイベント
    emit('update:modelValue', visible)
  }
})

/**
 * キーボードイベントのハンドリング
 */
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && internalVisible.value) {
    handleCancel()
  }
}

/**
 * モーダルを表示します
 * 外部コンポーネントから呼び出すためのパブリックメソッドです
 */
const showModal = () => {
  internalVisible.value = true
}

/**
 * モーダルを非表示にします
 * キャンセルボタンクリック時や外部からの制御に使用されます
 */
const hideModal = () => {
  internalVisible.value = false
}

/**
 * キャンセルボタンクリック時の処理
 * 親コンポーネントにcancelイベントを通知し、モーダルを閉じます
 */
const handleCancel = () => {
  emit('cancel')
  internalVisible.value = false
}

/**
 * 確認ボタンクリック時の処理
 * 親コンポーネントにconfirmイベントを通知します
 * モーダルの閉じる/閉じないは親コンポーネントが制御します
 */
const handleConfirm = () => {
  emit('confirm')
  // モーダルを自動的に閉じないように変更
  // 親コンポーネントでバリデーション後に手動でv-modelを制御する
}

onMounted(() => {
  // Escキーでモーダルを閉じる
  document.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  // イベントリスナーを削除
  document.removeEventListener('keydown', handleKeyDown)
})


// expose public methods
defineExpose({
  showModal,
  hideModal
})
</script>

<style lang="scss" scoped>
/* カスタムモーダルのスタイル */
.modal {
  z-index: 1050;
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-dialog {
  width: 100%;
  margin: 0;
  position: relative;

  &.modal-sm {
    max-width: 300px;
  }

  &.modal-md {
    max-width: 500px;
  }

  &.modal-lg {
    max-width: 800px;
  }

  &.modal-xl {
    max-width: 1140px;
  }
}

.modal-content {
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  background-color: white;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 0.5rem 0.5rem 0 0;

  // デフォルトスタイル
  &.header-default {
    background: #fff;
    color: #333;
  }

  // グラデーションスタイル
  &.header-gradient {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-bottom: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .btn-close {
      color: white;
      opacity: 0.8;

      &:hover {
        opacity: 1;
      }
    }
  }

  // ダークスタイル
  &.header-dark {
    background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
    color: white;
    border-bottom: 2px solid #10b981;

    .btn-close {
      color: white;
      opacity: 0.8;

      &:hover {
        opacity: 1;
      }
    }
  }
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: left;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  padding: 0;
  width: 1em;
  height: 1em;
  opacity: 0.5;
  cursor: pointer;
  margin-left: 1rem;
}

.btn-close:hover {
  opacity: 0.75;
}

.btn-close::before {
  content: "×";
}

.modal-body {
  padding: 1.5rem;
}

/* モーダルフッターのレイアウト */
.modal-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
}

/* キャンセルボタンのカスタムスタイル */
.btn-cancel {
  background-color: #fdece3;
  color: #d68461;
  border-color: #d68461;
  min-width: 100px;

  &:hover {
    background-color: #fce1d3;
    color: #c57652;
    border-color: #c57652;
  }

  &:focus,
  &:active {
    background-color: #fbd6c2;
    color: #b56743;
    border-color: #b56743;
    box-shadow: 0 0 0 0.2rem rgba(214, 132, 97, 0.25);
  }
}

/* OKボタンの幅調整 */
.btn-primary {
  min-width: 100px;
}

/* レスポンシブ対応 */
@media (max-width: 576px) {
  .modal {
    padding: 0.5rem;
  }

  .modal-dialog {
    max-width: none;
    width: 100%;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .modal-footer {
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
  }

  .btn-cancel,
  .btn-primary {
    width: 100%;
    min-width: auto;
  }
}
</style>
